<template>
    <div
        v-if="hasValue"
        class="flex py-section-sm px-4 md:px-12 gap-10 bg-neutral-50 rounded-none md:rounded-[60px]"
        :class="{'items-center flex-col lg:flex-row': hasOneStory, 'flex-col': !hasOneStory}"
    >
        <div
            class="flex w-full flex-col md:flex-row md:items-center gap-4 md:gap-0"
            :class="{ 'justify-between': !hasOneStory }"
        >
            <h2 class="font-roboto text-[32px] md:text-[44px] font-bold text-black leading-10 md:leading-[52px]">{{ title }}</h2>
            <UButton
                v-if="showSharedLink"
                :to="defaultLink"
                color="neutral"
                variant="link"
                size="sm"
                :label="linkText"
                :aria-label="`${linkText}: customer success stories`"
                trailing-icon="i-pkg-navigation-arrow-right"
                class="w-fit hover:!text-primary-500"
                :ui="{
                    base: 'hover:!text-primary-500 active:!text-primary-600',
                    label: 'whitespace-normal text-start',
                }"
            />
        </div>

        <div v-if="hasOneStory && firstStory" class="story w-full">
            <div class="flex flex-col md:flex-row bg-white rounded-2xl border border-neutral-50 overflow-hidden">
                <NuxtImg
                    format="webp"
                    quality="100"
                    placeholder
                    loading="lazy"
                    :src="$image(firstStory.image)"
                    :alt="firstStory.alt || ''"
                    class="mb-4 h-[385px] w-full object-cover rounded-t-lg md:mb-0 md:w-[238px] md:h-auto md:min-h-[200px] md:rounded-t-none md:rounded-bl-lg"
                />
                <div class="flex flex-col justify-center p-6 grow">
                    <div class="font-manrope text-neutral-900 related-resource-description" v-html="firstStory.description" />
                    <NuxtLink
                        v-if="singleStoryLinkTo"
                        :to="singleStoryLinkTo"
                        class="group btn md font text-base l-h-20 f-w-700 l-t-s-3 inline-flex justify-start plain mt-8 text-start p-0 w-fit"
                    >
                        <span class="font-manrope text-sm font-bold leading-4 tracking-[-0.3px] text-black transition-colors duration-300 group-hover:text-primary-500">
                            {{ linkText }}
                        </span>
                        <UIcon name="i-lucide-arrow-right" class="size-5 ml-1 shrink-0 text-black transition-colors duration-300 group-hover:text-primary-500" />
                    </NuxtLink>
                </div>
            </div>
        </div>
        <UCarousel
            v-else-if="variant === 'carousel'"
            v-slot="{ item }"
            :items="stories"
            arrows
            :ui="{
                container: 'items-stretch',
                item: 'basis-[85%] sm:basis-[48%] lg:basis-[31%] ps-4',
                prev: 'inset-s-0 sm:-inset-s-10',
                next: 'inset-e-0 sm:-inset-e-10',
            }"
            class="w-full"
        >
            <CustomerStoriesItem
                :show-link="showLink"
                :default-link="defaultLink"
                :only-use-explicit-uri="onlyUseExplicitUri"
                class="story h-full"
                :link-text="linkText"
                :story="item"
            />
        </UCarousel>
        <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            <CustomerStoriesItem
                v-for="story in stories"
                :key="story.alt || story.image"
                :show-link="showLink"
                :default-link="defaultLink"
                :only-use-explicit-uri="onlyUseExplicitUri"
                class="story h-full"
                :link-text="linkText"
                :story="story"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { CustomerStory } from '~/types/customer-story'
import CustomerStoriesItem from './CustomerStoriesItem.vue'

type CustomerStoriesVariant = 'grid' | 'carousel'

const props = withDefaults(defineProps<{
    defaultLink?: string
    linkText?: string
    onlyUseExplicitUri?: boolean
    /** When true, hide per-card links and show one shared CTA in the section header. */
    sharedLink?: boolean
    showLink?: boolean
    stories?: CustomerStory[]
    title?: string
    variant?: CustomerStoriesVariant
}>(), {
    defaultLink: '/case-studies',
    linkText: 'Read their story',
    onlyUseExplicitUri: false,
    sharedLink: false,
    showLink: true,
    stories: () => [],
    title: 'Our Customer Success Stories',
    variant: 'grid',
})

const hasValue = computed(() => props.stories.length > 0)
const hasOneStory = computed(() => props.stories.length === 1)
const showSharedLink = computed(() => (
    props.sharedLink && !hasOneStory.value && Boolean(props.defaultLink)
))

const firstStory = computed((): CustomerStory | null => (
    hasOneStory.value ? props.stories[0] ?? null : null
))

const singleStoryLinkTo = computed((): string | null => {
    const story = firstStory.value
    if (!story || !props.showLink) return null
    const uri = story.uri?.trim()
    const hasExplicit = Boolean(uri && uri !== '/')
    if (props.onlyUseExplicitUri) return hasExplicit ? uri ?? null : null
    if (hasExplicit) return uri ?? null
    return props.defaultLink || null
})
</script>

<style scoped>
:deep(.related-resource-description a) {
    color: inherit;
    text-decoration: none;
}

:deep(.related-resource-description a:hover) {
    text-decoration: underline;
}
</style>
